// src/components/security/ActiveSessions.jsx — Session & Device Management
import React, { useEffect, useState, useCallback } from "react";
import { Monitor, Smartphone, Tablet, Globe, Shield, LogOut, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import "./activeSessions.css";

function formatRelativeTime(isoString) {
  if (!isoString) return "Recently";
  const diffMs = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 2) return "Active now";
  if (mins < 60) return `Last active ${mins} min${mins > 1 ? "s" : ""} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Last active ${hours} hr${hours > 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  return `Last active ${days} day${days > 1 ? "s" : ""} ago`;
}

function getDeviceIcon(deviceType) {
  switch (deviceType) {
    case "mobile":
      return <Smartphone size={20} className="crm-device-icon mobile" />;
    case "tablet":
      return <Tablet size={20} className="crm-device-icon tablet" />;
    default:
      return <Monitor size={20} className="crm-device-icon desktop" />;
  }
}

export default function ActiveSessions() {
  const { logout } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);
  const [revokingOthers, setRevokingOthers] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, type: null, sessionId: null, title: "" });

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/sessions");
      if (Array.isArray(res.data)) {
        setSessions(res.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load active sessions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleRevokeSingle = async (sessionId) => {
    setRevokingId(sessionId);
    // Optimistic UI update
    const prevSessions = [...sessions];
    setSessions(prev => prev.filter(s => s.id !== sessionId));

    try {
      await api.delete(`/sessions/${sessionId}`);
      toast.success("Session logged out successfully");
    } catch (err) {
      setSessions(prevSessions); // Rollback on failure
      toast.error(err?.response?.data?.message || "Failed to log out session");
    } finally {
      setRevokingId(null);
      setConfirmModal({ open: false, type: null, sessionId: null, title: "" });
    }
  };

  const handleRevokeOthers = async () => {
    setRevokingOthers(true);
    // Optimistic update: keep only current session
    const prevSessions = [...sessions];
    setSessions(prev => prev.filter(s => s.isCurrent));

    try {
      await api.delete("/sessions/others");
      toast.success("Logged out from all other devices");
    } catch (err) {
      setSessions(prevSessions); // Rollback on failure
      toast.error(err?.response?.data?.message || "Failed to log out other devices");
    } finally {
      setRevokingOthers(false);
      setConfirmModal({ open: false, type: null, sessionId: null, title: "" });
    }
  };

  const otherSessionsCount = sessions.filter(s => !s.isCurrent).length;

  return (
    <div className="crm-sessions-container">
      {/* Header Toolbar */}
      <div className="crm-sessions-header">
        <div>
          <h3 className="crm-sessions-title">
            <Shield size={18} className="text-primary" /> Active Sessions & Devices
          </h3>
          <p className="crm-sessions-subtitle">
            Devices currently logged into your account. You can log out of any session at any time.
          </p>
        </div>

        <div className="crm-sessions-header-actions">
          <button
            type="button"
            className="crm-session-btn-refresh"
            onClick={loadSessions}
            disabled={loading}
            title="Refresh sessions"
          >
            <RefreshCw size={14} className={loading ? "spin" : ""} /> Refresh
          </button>

          {otherSessionsCount > 0 && (
            <button
              type="button"
              className="crm-session-btn-revoke-all"
              onClick={() => setConfirmModal({
                open: true,
                type: "others",
                title: "Log out of all other devices?",
                message: `This will immediately terminate ${otherSessionsCount} other active session${otherSessionsCount > 1 ? "s" : ""}. You will remain signed in on this device.`
              })}
              disabled={revokingOthers}
            >
              <LogOut size={14} /> Log out all other devices
            </button>
          )}
        </div>
      </div>

      {/* Sessions List */}
      {loading && sessions.length === 0 ? (
        <div className="crm-sessions-loading">
          <RefreshCw size={22} className="spin text-muted" />
          <span>Loading active sessions...</span>
        </div>
      ) : sessions.length === 0 ? (
        <div className="crm-sessions-empty">
          <Shield size={32} className="text-muted" />
          <p>No active sessions found.</p>
        </div>
      ) : (
        <div className="crm-sessions-list">
          {sessions.map((sess) => (
            <div key={sess.id} className={`crm-session-card ${sess.isCurrent ? "is-current" : ""}`}>
              <div className="crm-session-icon-wrap">
                {getDeviceIcon(sess.device_type)}
              </div>

              <div className="crm-session-details">
                <div className="crm-session-main-row">
                  <span className="crm-session-device-name">
                    {sess.browser || "Browser"} on {sess.os || "Device"}
                  </span>

                  {sess.isCurrent ? (
                    <span className="crm-session-badge-current">
                      <CheckCircle2 size={12} /> This device
                    </span>
                  ) : (
                    <span className="crm-session-time-text">
                      {formatRelativeTime(sess.last_active_at)}
                    </span>
                  )}
                </div>

                <div className="crm-session-meta-row">
                  <span className="crm-session-meta-item">
                    <Globe size={13} className="text-muted" /> {sess.location || "Unknown Location"}
                  </span>
                  <span className="crm-session-meta-separator">•</span>
                  <span className="crm-session-meta-item">
                    IP: <code>{sess.ip_address || "Unknown IP"}</code>
                  </span>
                  {sess.isCurrent && (
                    <>
                      <span className="crm-session-meta-separator">•</span>
                      <span className="crm-session-meta-active-indicator">
                        <span className="crm-session-dot-green" /> Active now
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="crm-session-actions">
                {sess.isCurrent ? (
                  <button
                    type="button"
                    className="crm-session-btn-logout-current"
                    onClick={() => logout()}
                    title="Log out of PAJ CRM on this device"
                  >
                    Log out
                  </button>
                ) : (
                  <button
                    type="button"
                    className="crm-session-btn-revoke"
                    onClick={() => setConfirmModal({
                      open: true,
                      type: "single",
                      sessionId: sess.id,
                      title: "Log out this session?",
                      message: `Are you sure you want to log out ${sess.browser} on ${sess.os} (${sess.ip_address})?`
                    })}
                    disabled={revokingId === sess.id}
                  >
                    {revokingId === sess.id ? "Logging out..." : "Log out"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation Dialog Modal */}
      {confirmModal.open && (
        <div className="crm-session-modal-overlay" onClick={() => setConfirmModal({ open: false, type: null, sessionId: null, title: "" })}>
          <div className="crm-session-modal-box" onClick={e => e.stopPropagation()}>
            <div className="crm-session-modal-header">
              <div className="crm-session-modal-icon-wrap">
                <AlertTriangle size={20} className="text-warning" />
              </div>
              <h4>{confirmModal.title}</h4>
            </div>

            <p className="crm-session-modal-desc">{confirmModal.message}</p>

            <div className="crm-session-modal-footer">
              <button
                type="button"
                className="crm-session-btn-cancel"
                onClick={() => setConfirmModal({ open: false, type: null, sessionId: null, title: "" })}
              >
                Cancel
              </button>
              <button
                type="button"
                className="crm-session-btn-confirm-revoke"
                onClick={() => {
                  if (confirmModal.type === "others") {
                    handleRevokeOthers();
                  } else if (confirmModal.sessionId) {
                    handleRevokeSingle(confirmModal.sessionId);
                  }
                }}
              >
                Confirm Log Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
